import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Pencil, KeyRound, X, Check, UserPlus } from "lucide-react";
import { AddClientDialog } from "@/components/AddClientDialog";

const AdminClients = () => {
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);
    const [pwUser, setPwUser] = useState(null);
    const [addOpen, setAddOpen] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/users");
            setClients(data);
        } catch {
            toast.error("Failed to load clients");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const saved = () => {
        setEditing(null);
        load();
    };

    return (
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12" data-testid="admin-clients">
            <div className="flex flex-wrap items-end justify-between gap-6 mb-8">
                <div>
                    <div className="un-label text-un-grape mb-3">Admin · Clients</div>
                    <h1 className="un-h1 text-5xl md:text-6xl">
                        Client <span className="text-un-berry">Accounts</span>.
                    </h1>
                </div>
                <button
                    onClick={() => setAddOpen(true)}
                    className="un-btn-outline text-sm flex items-center gap-2"
                    data-testid="admin-clients-add-button"
                >
                    <UserPlus className="w-4 h-4" /> Add One Client
                </button>
            </div>

            {loading ? (
                <div className="un-card p-10 flex items-center gap-3 justify-center">
                    <Loader2 className="w-5 h-5 animate-spin text-un-berry" />
                    <span className="un-label text-un-grape">Loading…</span>
                </div>
            ) : clients.length === 0 ? (
                <div className="un-card p-10 text-center">
                    <div className="un-h3 mb-2">No Clients Yet</div>
                    <div className="text-un-onyx/60 text-sm">
                        Issue an activation code, then share it with your invitee.
                    </div>
                </div>
            ) : (
                <div className="border-2 border-un-onyx overflow-x-auto relative" data-testid="clients-table">
                    <div className="md:hidden un-mono text-[10px] text-un-onyx/60 px-3 py-1 border-b-2 border-un-onyx/20 bg-un-water/10">
                        ← Swipe to see all columns →
                    </div>
                    <table className="w-full text-left min-w-[900px]">
                        <thead className="bg-un-onyx text-white">
                            <tr>
                                {["Name", "Email", "Tag", "This Month", "All-Time Generations", "Projects", "Monthly Limit", "Access Expires", "Status", ""].map((h) => {
                                    const centered = ["This Month", "All-Time Generations", "Projects", "Monthly Limit"].includes(h);
                                    return (
                                        <th
                                            key={h}
                                            className={`un-label px-4 py-3 border-r-2 border-white/20 last:border-r-0 whitespace-nowrap ${centered ? "text-center" : ""}`}
                                        >
                                            {h}
                                        </th>
                                    );
                                })}
                            </tr>
                        </thead>
                        <tbody>
                            {clients.map((c) => {
                                const iso = c.access_expires_at;
                                const dl = iso ? Math.ceil((new Date(iso).getTime() - Date.now()) / (1000 * 60 * 60 * 24)) : null;
                                const dlColor = dl == null ? "text-un-onyx/40" : dl < 0 ? "text-red-600 font-bold" : dl <= 7 ? "text-un-berry font-bold" : dl <= 30 ? "text-orange-500" : "text-un-onyx/70";
                                const limit = c.monthly_script_limit;
                                const usedMo = c.scripts_this_month || 0;
                                const overLimit = limit && usedMo >= limit;
                                return (
                                <tr
                                    key={c.id}
                                    className="border-t-2 border-un-onyx"
                                    data-testid={`client-row-${c.id}`}
                                >
                                    <td className="px-4 py-4 font-semibold whitespace-nowrap">
                                        {(c.first_name || c.last_name)
                                            ? `${c.first_name || ""} ${c.last_name || ""}`.trim()
                                            : c.name || "—"}
                                    </td>
                                    <td className="px-4 py-4 un-mono text-sm">{c.email}</td>
                                    <td className="px-4 py-4">
                                        {c.tag ? (
                                            <span className="un-mono text-xs bg-un-water/30 border-2 border-un-onyx px-2 py-1 inline-block">
                                                {c.tag}
                                            </span>
                                        ) : (
                                            <span className="text-un-onyx/40">—</span>
                                        )}
                                    </td>
                                    <td className="px-4 py-4 un-mono text-xs whitespace-nowrap text-center">
                                        <span className={overLimit ? "text-red-600 font-bold text-base" : "font-bold text-base"}>{usedMo}</span>
                                        {c.last_generated_at && (
                                            <div className="text-[10px] text-un-onyx/50 mt-1">
                                                Last: {new Date(c.last_generated_at).toLocaleDateString()}
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-4 py-4 un-mono text-xs whitespace-nowrap text-center">
                                        <span className="font-bold text-base">{c.scripts_total || 0}</span>
                                    </td>
                                    <td className="px-4 py-4 un-mono text-xs whitespace-nowrap text-center">
                                        <span className="font-bold text-base">{c.projects_total || 0}</span>
                                    </td>
                                    <td className="px-4 py-4 un-mono text-xs whitespace-nowrap text-center">
                                        {limit ? (
                                            <>
                                                <span className={overLimit ? "text-red-600 font-bold" : "font-bold"}>{limit}</span>
                                                <span className="text-un-onyx/60"> / mo</span>
                                            </>
                                        ) : (
                                            <span className="text-un-onyx/50">Unlimited</span>
                                        )}
                                    </td>
                                    <td className={`px-4 py-4 un-mono text-xs whitespace-nowrap ${dlColor}`}>
                                        {iso ? (
                                            <>
                                                <div>{new Date(iso).toLocaleDateString()}</div>
                                                <div className="text-[10px]">
                                                    {dl < 0 ? "expired" : `${dl}d left`}
                                                </div>
                                            </>
                                        ) : "no expiry"}
                                    </td>
                                    <td className="px-4 py-4">
                                        <span
                                            className={`un-pill ${c.status === "active" ? "un-pill-lime" : "un-pill-outline"}`}
                                        >
                                            {c.status}
                                        </span>
                                    </td>
                                    <td className="px-4 py-4 text-right space-x-2 whitespace-nowrap">
                                        <button
                                            onClick={() => setPwUser(c)}
                                            className="un-btn-ghost !px-2 inline-flex items-center gap-1 text-xs"
                                            data-testid={`client-reset-pw-${c.id}`}
                                            title="Reset password"
                                        >
                                            <KeyRound className="w-3.5 h-3.5" /> Reset PW
                                        </button>
                                        <button
                                            onClick={() => setEditing(c)}
                                            className="un-btn-outline text-xs inline-flex items-center gap-1"
                                            data-testid={`client-edit-${c.id}`}
                                        >
                                            <Pencil className="w-3.5 h-3.5" /> Edit
                                        </button>
                                    </td>
                                </tr>
                            );})}
                        </tbody>
                    </table>
                </div>
            )}

            {editing && <EditDialog client={editing} onClose={() => setEditing(null)} onSaved={saved} />}
            {pwUser && <PasswordDialog client={pwUser} onClose={() => setPwUser(null)} />}
            {addOpen && <AddClientDialog onClose={() => setAddOpen(false)} onCreated={load} />}
        </div>
    );
};

const PRESETS = [
    { key: "7d", label: "7 Days" },
    { key: "14d", label: "14 Days" },
    { key: "30d", label: "30 Days" },
    { key: "3mo", label: "3 Months" },
    { key: "6mo", label: "6 Months" },
];

const EditDialog = ({ client, onClose, onSaved }) => {
    const [form, setForm] = useState({
        first_name: client.first_name || "",
        last_name: client.last_name || "",
        email: client.email || "",
        phone: client.phone || "",
        status: client.status || "active",
        access_expires_at: client.access_expires_at ? client.access_expires_at.split("T")[0] : "",
        preset_duration: "",
        monthly_script_limit: client.monthly_script_limit || "",
    });
    const [saving, setSaving] = useState(false);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const pickPreset = (key) => {
        setForm((f) => ({ ...f, preset_duration: key, access_expires_at: "" }));
    };

    const save = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = {
                first_name: form.first_name,
                last_name: form.last_name,
                email: form.email,
                phone: form.phone,
                status: form.status,
            };
            // Monthly script limit: convert to int; 0 or blank = unlimited
            if (form.monthly_script_limit !== "" && form.monthly_script_limit !== null) {
                payload.monthly_script_limit = parseInt(form.monthly_script_limit, 10) || 0;
            } else if (client.monthly_script_limit) {
                // User cleared the field — send 0 to reset to unlimited
                payload.monthly_script_limit = 0;
            }
            // Only send access changes if one is set
            if (form.preset_duration) {
                payload.preset_duration = form.preset_duration;
            } else if (form.access_expires_at !== (client.access_expires_at ? client.access_expires_at.split("T")[0] : "")) {
                payload.access_expires_at = form.access_expires_at || null;
            }
            await api.patch(`/admin/users/${client.id}`, payload);
            toast.success("Client updated");
            onSaved();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Update failed");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-un-onyx/70"
            role="dialog"
            aria-modal="true"
            onClick={onClose}
            data-testid="client-edit-dialog"
        >
            <form
                onSubmit={save}
                onClick={(e) => e.stopPropagation()}
                className="un-card max-w-lg w-full bg-white max-h-[90vh] overflow-y-auto"
            >
                <div className="flex items-center justify-between border-b-2 border-un-onyx px-6 py-4 sticky top-0 bg-white z-10">
                    <div className="un-label text-un-grape">Edit Client</div>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close"
                        className="un-btn-ghost !px-1 !py-1"
                        data-testid="client-edit-close"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>
                <div className="p-6 space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <label className="block">
                            <span className="un-label text-un-onyx block mb-2">First Name</span>
                            <input
                                type="text"
                                value={form.first_name}
                                onChange={set("first_name")}
                                className="un-input"
                                data-testid="client-edit-first-name"
                            />
                        </label>
                        <label className="block">
                            <span className="un-label text-un-onyx block mb-2">Last Name</span>
                            <input
                                type="text"
                                value={form.last_name}
                                onChange={set("last_name")}
                                className="un-input"
                                data-testid="client-edit-last-name"
                            />
                        </label>
                    </div>
                    <label className="block">
                        <span className="un-label text-un-onyx block mb-2">Email</span>
                        <input
                            type="email"
                            value={form.email}
                            onChange={set("email")}
                            className="un-input"
                            data-testid="client-edit-email"
                        />
                    </label>
                    <label className="block">
                        <span className="un-label text-un-onyx block mb-2">Phone</span>
                        <input
                            type="tel"
                            value={form.phone}
                            onChange={set("phone")}
                            className="un-input"
                            data-testid="client-edit-phone"
                        />
                    </label>
                    <label className="block">
                        <span className="un-label text-un-onyx block mb-2">Status</span>
                        <select
                            value={form.status}
                            onChange={set("status")}
                            className="un-input"
                            data-testid="client-edit-status"
                        >
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                        </select>
                    </label>
                    <div>
                        <span className="un-label text-un-onyx block mb-2">Extend Access</span>
                        <div className="flex flex-wrap gap-2 mb-3">
                            {PRESETS.map((p) => (
                                <button
                                    key={p.key}
                                    type="button"
                                    onClick={() => pickPreset(p.key)}
                                    className={`text-xs font-bold px-3 py-2 border-2 border-un-onyx ${form.preset_duration === p.key ? "bg-un-berry text-white" : "bg-white text-un-onyx hover:bg-un-onyx hover:text-white"}`}
                                    data-testid={`edit-preset-${p.key}`}
                                >
                                    +{p.label}
                                </button>
                            ))}
                        </div>
                        <label className="block">
                            <span className="un-label text-un-onyx block mb-2 text-xs">Or Set Exact Expiry Date</span>
                            <input
                                type="date"
                                value={form.access_expires_at}
                                onChange={(e) => setForm((f) => ({ ...f, access_expires_at: e.target.value, preset_duration: "" }))}
                                className="un-input"
                                min={new Date().toISOString().split("T")[0]}
                                data-testid="client-edit-expires"
                            />
                            <span className="text-xs text-un-onyx/60 mt-1 block">
                                Leave blank to keep the current expiry. Clear the field to set no expiry.
                            </span>
                        </label>
                    </div>
                    <div className="un-divider pt-6">
                        <label className="block">
                            <span className="un-label text-un-onyx block mb-2">Monthly Generation Limit</span>
                            <input
                                type="number"
                                min="0"
                                value={form.monthly_script_limit}
                                onChange={(e) => setForm((f) => ({ ...f, monthly_script_limit: e.target.value }))}
                                className="un-input"
                                placeholder="Leave blank for unlimited"
                                data-testid="client-edit-monthly-limit"
                            />
                            <span className="text-xs text-un-onyx/60 mt-1 block">
                                Max generations this client can run per calendar month. Each generation produces 3 variants. Leave blank or set to 0 for unlimited. Resets on the 1st.
                            </span>
                        </label>
                    </div>
                </div>
                <div className="px-6 pb-6 flex items-center justify-end gap-3">
                    <button type="button" onClick={onClose} className="un-btn-outline text-sm">
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={saving}
                        className="un-btn-primary text-sm flex items-center gap-2"
                        data-testid="client-edit-save"
                    >
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                        Save Changes
                    </button>
                </div>
            </form>
        </div>
    );
};

const PasswordDialog = ({ client, onClose }) => {
    const [pw, setPw] = useState("");
    const [saving, setSaving] = useState(false);

    const save = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await api.post(`/admin/users/${client.id}/reset-password`, { new_password: pw });
            toast.success("Password reset. Share the new password with the client.");
            onClose();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Reset failed");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-un-onyx/70"
            role="dialog"
            aria-modal="true"
            onClick={onClose}
            data-testid="client-pw-dialog"
        >
            <form
                onSubmit={save}
                onClick={(e) => e.stopPropagation()}
                className="un-card max-w-md w-full bg-white"
            >
                <div className="flex items-center justify-between border-b-2 border-un-onyx px-6 py-4">
                    <div className="un-label text-un-grape">Reset Password</div>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close"
                        className="un-btn-ghost !px-1 !py-1"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>
                <div className="p-6">
                    <p className="text-sm text-un-onyx/70 mb-4">
                        Set a new password for{" "}
                        <strong>
                            {(client.first_name || client.last_name)
                                ? `${client.first_name || ""} ${client.last_name || ""}`.trim()
                                : client.email}
                        </strong>
                        . Minimum 10 chars, must include uppercase, lowercase, and a number.
                    </p>
                    <label className="block">
                        <span className="un-label text-un-onyx block mb-2">New Password</span>
                        <input
                            type="text"
                            value={pw}
                            onChange={(e) => setPw(e.target.value)}
                            className="un-input un-mono"
                            required
                            minLength={10}
                            data-testid="client-pw-input"
                            autoFocus
                        />
                    </label>
                </div>
                <div className="px-6 pb-6 flex items-center justify-end gap-3">
                    <button type="button" onClick={onClose} className="un-btn-outline text-sm">
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={saving}
                        className="un-btn-primary text-sm flex items-center gap-2"
                        data-testid="client-pw-save"
                    >
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
                        Set Password
                    </button>
                </div>
            </form>
        </div>
    );
};

export default AdminClients;
